import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCalculator, 
  faUniversity, 
  faMoneyBillWave, 
  faPlus,
  faFileInvoice,
  faMoneyCheckAlt,
  faBalanceScale,
  faCheck,
  faExclamationCircle
} from '@fortawesome/free-solid-svg-icons';
import { getAmountClass } from '../utils/formatters';
import RegularizationSection from './accounting/RegularizationSection';
import IndicatorsSection from './accounting/IndicatorsSection';

// New 3-column layout component
const AccountingSummary = ({ finance, bail }) => {
  if (!finance || !bail) return <article aria-busy="true">Aucune donnée financière disponible</article>;
  
  // Determine which balances to display based on lease termination status
  const displayStandardBalance = bail.estTermine ? finance.soldes.final : finance.soldes.courant;
  const displayV2Balance = bail.estTermine ? finance.soldes.finalV2 : finance.soldes.courantV2;
  
  // Calculate derived values
  const standardBalanceClass = getAmountClass(displayStandardBalance);
  const v2BalanceClass = getAmountClass(displayV2Balance);
  const combinedBalance = (displayStandardBalance || 0) + (displayV2Balance || 0);
  const combinedBalanceClass = getAmountClass(combinedBalance);
  const virementEffectueClass = getAmountClass(finance.regularisation?.virementEffectue);
  const classReste = getAmountClass(finance.regularisation?.resteArembourser);
  
  // Boolean flag for payment sufficiency
  const paymentsSuffisant = Number(finance.totaux.paiements.total) >= Number(finance.totaux.factures.total);
  
  // Get absolute values for display
  const virementEffectueAbs = Math.abs(finance.regularisation?.virementEffectue || 0);
  const resteArembourserAbs = Math.abs(finance.regularisation?.resteArembourser || 0);

  // Get factureCloture values
  const factureClotureStandard = finance.totaux.factures.details.factureCloture || 0;
  const factureClotureV2 = finance.totaux.factures.details.factureClotureV2 || 0;

  return (
    <article className="bloc">
      <header>
        <h2>
          <FontAwesomeIcon icon={faCalculator} /> Synthèse comptable
        </h2>
        
        <div className="grid">
          <nav role="group">
            <kbd className="accounting-badge standard">
              <FontAwesomeIcon icon={faUniversity} /> Standard
            </kbd>
            <kbd className="accounting-badge v2">
              <FontAwesomeIcon icon={faMoneyBillWave} /> V2
            </kbd>
            <kbd className="secondary">
              <FontAwesomeIcon icon={faPlus} /> Combiné
            </kbd>
          </nav>
        </div>
      </header>
      
      {/* New 3-column table layout */}
      <table className="resume-table">
        <thead>
          <tr className="accounting-section">
            <th className="label" style={{ width: '40%', color: 'white', fontWeight: 'bold' }}>
              <FontAwesomeIcon icon={faFileInvoice} /> FACTURES
            </th>
            <th style={{ width: '30%', textAlign: 'center', color: 'white', fontWeight: 'bold' }}>Standard</th>
            <th style={{ width: '30%', textAlign: 'center', color: 'white', fontWeight: 'bold' }}>V2</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="label">Factures caution</td>
            <td style={{ textAlign: 'center' }}>{finance.totaux.factures.details.caution} €</td>
            <td style={{ textAlign: 'center' }}>-</td>
          </tr>
          <tr>
            <td className="label">Factures frais de dossier</td>
            <td style={{ textAlign: 'center' }}>{finance.totaux.factures.details.fraisDossier} €</td>
            <td style={{ textAlign: 'center' }}>{finance.totaux.factures.details.fraisDossierV2} €</td>
          </tr>
          <tr>
            <td className="label">Factures loyer</td>
            <td style={{ textAlign: 'center' }}>{finance.totaux.factures.details.loyer} €</td>
            <td style={{ textAlign: 'center' }}>{finance.totaux.factures.details.loyerV2} €</td>
          </tr>
          <tr>
            <td className="label">Remises</td>
            <td style={{ textAlign: 'center' }}>{finance.totaux.factures.details.remise || 0} €</td>
            <td style={{ textAlign: 'center' }}>{finance.totaux.factures.details.remiseV2 || 0} €</td>
          </tr>
          <tr>
            <td className="label">Factures clôture</td>
            <td style={{ textAlign: 'center' }}>{factureClotureStandard} €</td>
            <td style={{ textAlign: 'center' }}>{factureClotureV2} €</td>
          </tr>
          <tr>
            <td className="label">Réparations</td>
            <td style={{ textAlign: 'center' }}>{finance.totaux.factures.details.reparations || 0} €</td>
            <td style={{ textAlign: 'center' }}>{finance.totaux.factures.details.reparationsV2 || 0} €</td>
          </tr>
          <tr className="highlight-row">
            <td className="label"><strong>Total factures</strong></td>
            <td style={{ textAlign: 'center' }}><strong>{finance.totaux.factures.regular} €</strong></td>
            <td style={{ textAlign: 'center' }}><strong>{finance.totaux.factures.v2} €</strong></td>
          </tr>
          <tr className="highlight-row">
            <td className="label"><strong>Total factures combiné</strong></td>
            <td colSpan="2" style={{ textAlign: 'center' }}><strong>{finance.totaux.factures.total} €</strong></td>
          </tr>
        </tbody>
      </table>
      
      <table className="resume-table" style={{ marginTop: '1rem' }}>
        <thead>
          <tr className="accounting-section">
            <th className="label" style={{ width: '40%', color: 'white', fontWeight: 'bold' }}>
              <FontAwesomeIcon icon={faMoneyCheckAlt} /> PAIEMENTS
            </th>
            <th style={{ width: '30%', textAlign: 'center', color: 'white', fontWeight: 'bold' }}>Standard</th>
            <th style={{ width: '30%', textAlign: 'center', color: 'white', fontWeight: 'bold' }}>V2</th>
          </tr>
        </thead>
        <tbody>
          <tr className="highlight-row">
            <td className="label"><strong>Total règlements</strong></td>
            <td style={{ textAlign: 'center' }}><strong>{finance.totaux.paiements.regular} €</strong></td>
            <td style={{ textAlign: 'center' }}><strong>{finance.totaux.paiements.v2} €</strong></td>
          </tr>
          <tr className="highlight-row">
            <td className="label"><strong>Total règlements combiné</strong></td>
            <td colSpan="2" style={{ textAlign: 'center' }}>
              <strong>
                {finance.totaux.paiements.total} € 
                {paymentsSuffisant ? (
                  <FontAwesomeIcon icon={faCheck} style={{ color: '#16a34a', marginLeft: '5px' }} />
                ) : (
                  <FontAwesomeIcon icon={faExclamationCircle} style={{ color: '#dc2626', marginLeft: '5px' }} />
                )}
              </strong>
            </td>
          </tr>
        </tbody>
      </table>
      
      <table className="resume-table" style={{ marginTop: '1rem' }}>
        <thead>
          <tr className="accounting-section">
            <th className="label" style={{ width: '40%', color: 'white', fontWeight: 'bold' }}>
              <FontAwesomeIcon icon={faBalanceScale} /> SOLDES
            </th>
            <th style={{ width: '30%', textAlign: 'center', color: 'white', fontWeight: 'bold' }}>Standard</th>
            <th style={{ width: '30%', textAlign: 'center', color: 'white', fontWeight: 'bold' }}>V2</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="label">Solde {bail.estTermine ? 'final' : 'courant'} (base)</td>
            <td className={`${standardBalanceClass}`} style={{ textAlign: 'center' }}>{displayStandardBalance} €</td>
            <td className={`${v2BalanceClass}`} style={{ textAlign: 'center' }}>{displayV2Balance} €</td>
          </tr>
          <tr className="highlight-row">
            <td className="label"><strong>Solde combiné</strong></td>
            <td colSpan="2" className={`${combinedBalanceClass}`} style={{ textAlign: 'center' }}>
              <strong>{combinedBalance} €</strong>
            </td>
          </tr>
        </tbody>
      </table>
      
      <table className="resume-table" style={{ marginTop: '1rem' }}>
        <tbody>
          <RegularizationSection 
            regularisation={finance.regularisation}
            virementEffectueClass={virementEffectueClass}
            classReste={classReste}
            virementEffectueAbs={virementEffectueAbs}
            resteArembourserAbs={resteArembourserAbs}
          />
          <IndicatorsSection indicateurs={finance.indicateurs} />
        </tbody>
      </table>
      
      <footer>
        <details>
          <summary>Légende des montants</summary>
          <dl>
            <dt><mark class="positive">Solde positif (vert)</mark></dt>
            <dd>Montant en faveur du locataire</dd>
            
            <dt><mark class="negative">Solde négatif (rouge)</mark></dt>
            <dd>Montant dû par le locataire</dd>
            
            <dt>Solde Standard</dt>
            <dd>Différence entre paiements et factures standards</dd>
            
            <dt>Solde V2</dt>
            <dd>Différence entre paiements et factures V2</dd>
            
            <dt>Solde final</dt>
            <dd>Montant final après toutes régularisations (caution, etc.)</dd>
          </dl>
        </details>
      </footer>
    </article>
  );
};

export default AccountingSummary;